import { Card, CardContent, CardHeader, List, ListItem, ListItemText, Typography, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { fetchMatches, fetchTournaments } from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { canManageTournament } from '../../utils/tournamentPermissions';

const UpcomingMatches = () => {
  const { user } = useAuth();
  const { data: matches, isLoading: matchesLoading } = useQuery(['matches'], () => fetchMatches());
  const { data: tournaments, isLoading: tournamentsLoading } = useQuery(['tournaments'], fetchTournaments);
  const navigate = useNavigate();

  if (matchesLoading || tournamentsLoading) return <Card><CardContent>Loading...</CardContent></Card>;

  const userRole = user?.['custom:role'];
  const userId = user?.userId;
  const isAdmin = userRole === 'admin';
  const isTournamentManager = userRole === 'tournament_manager';

  // Filter matches based on user role
  let filteredMatches = (matches || []).filter(m => m.status === 'scheduled');

  if (!isAdmin) {
    if (isTournamentManager) {
      // Tournament managers see matches for tournaments they manage
      const managedTournamentIds = (tournaments || [])
        .filter(t => canManageTournament(user, t))
        .map(t => t.id);
      
      filteredMatches = filteredMatches.filter(m => 
        managedTournamentIds.includes(m.tournament_id)
      );
    } else {
      // Regular users see only their own matches
      filteredMatches = filteredMatches.filter(m => 
        m.player1_id === userId || 
        m.player2_id === userId ||
        m.player1_team_id === userId ||
        m.player2_team_id === userId
      );
    }
  }

  const getScheduledTime = (match: any) => match.scheduled_time || match.scheduled_at;

  const upcomingMatches = filteredMatches
    .sort((a, b) => {
      const aTime = getScheduledTime(a);
      const bTime = getScheduledTime(b);
      // Sort by scheduled time if available
      if (aTime && bTime) {
        return new Date(aTime).getTime() - new Date(bTime).getTime();
      }
      // Put matches with scheduled times first
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;
      return 0;
    });

  // Group matches by tournament
  const matchesByTournament = upcomingMatches.reduce((acc: any, match) => {
    const tournamentId = match.tournament_id;
    if (!acc[tournamentId]) {
      acc[tournamentId] = [];
    }
    acc[tournamentId].push(match);
    return acc;
  }, {});

  // Get tournament names
  const getTournamentName = (tournamentId: string) => {
    const tournament = tournaments?.find(t => t.id === tournamentId);
    return tournament?.name || 'Unknown Tournament';
  };

  return (
    <Card>
      <CardHeader title="Upcoming Matches" />
      <CardContent>
        {upcomingMatches.length === 0 ? (
          <Typography color="text.secondary">No upcoming matches</Typography>
        ) : (
          <Box>
            {Object.entries(matchesByTournament).map(([tournamentId, tournamentMatches]: [string, any]) => (
              <Accordion key={tournamentId} defaultExpanded={Object.keys(matchesByTournament).length === 1}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {getTournamentName(tournamentId)} ({tournamentMatches.length} matches)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {tournamentMatches.map((match: any) => (
                      <ListItem 
                        key={match.id}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                        onClick={() => navigate(`/tournaments/${match.tournament_id}`)}
                      >
                        <ListItemText
                          primary={`Round ${match.round} - Match ${match.match_number}`}
                          secondary={
                            (match.scheduled_time || match.scheduled_at)
                              ? format(new Date(match.scheduled_time || match.scheduled_at || ''), 'MMM dd, yyyy h:mm a')
                              : 'Time TBD'
                          }
                        />
                        {match.court && (
                          <Typography variant="body2" color="text.secondary">
                            Court {match.court}
                          </Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingMatches;
